import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'
import { fetchAll } from './lib/fetchAll'
import Auth from './components/Auth'
import Dashboard from './components/Dashboard'
import TicketList from './components/TicketList'
import TicketDetail from './components/TicketDetail'
import NewTicket from './components/NewTicket'
import Settings from './components/Settings'
import Customers from './components/Customers'
import { Icon, Avatar, Spinner, normStatus } from './components/ui'

// Viste elenco: ambito (tutti / miei / non assegnati) ed eventuale stato fisso
export const LIST_VIEWS = {
  tickets:      { title: 'Tutti i ticket', subtitle: 'Tutte le richieste di assistenza', scope: 'all' },
  'my-tickets': { title: 'I miei ticket', subtitle: 'Richieste assegnate a te', scope: 'mine' },
  unassigned:   { title: 'Non assegnati', subtitle: 'Richieste ancora da prendere in carico', scope: 'unassigned' },
  open:         { title: 'Aperti', subtitle: 'Richieste in attesa di essere lavorate', scope: 'all', status: 'aperto' },
  progress:     { title: 'In lavorazione', subtitle: 'Richieste su cui si sta lavorando', scope: 'all', status: 'in_lavorazione' },
  resolved:     { title: 'Risolti', subtitle: 'Richieste chiuse', scope: 'all', status: 'risolto' },
}

export default function App() {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [view, setView] = useState('dashboard')
  const [listView, setListView] = useState('tickets')
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const [counts, setCounts] = useState(null)
  const [returnView, setReturnView] = useState('tickets')   // dove torna "indietro" dal ticket
  const [customerId, setCustomerId] = useState(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) loadProfile(session.user.id)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      if (!session) { setProfile(null); setLoading(false); return }
      // Il rinnovo del token (ogni ora) non cambia l'utente: il profilo non si ricarica
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') loadProfile(session.user.id)
    })
    return () => subscription.unsubscribe()
  }, [])

  const loadProfile = async (userId) => {
    const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single()
    if (error) console.error('Errore caricamento profilo:', error.message)
    // Un errore momentaneo di rete non deve "buttare fuori" chi sta lavorando
    setProfile(prev => data || (prev && prev.id === userId ? prev : null))
    setLoading(false)
  }

  // Contatori delle viste nella barra laterale (si aggiornano a ogni cambio di schermata)
  useEffect(() => {
    if (!profile) return
    let alive = true
    fetchAll(() => supabase.from('tickets').select('status, assigned_to').order('id')).then(({ data }) => {
      if (!alive || !data) return
      const c = { tickets: data.length, 'my-tickets': 0, unassigned: 0, open: 0, progress: 0, resolved: 0 }
      data.forEach(t => {
        const st = normStatus(t.status)
        if (t.assigned_to === profile.id) c['my-tickets']++
        if (!t.assigned_to && st !== 'risolto') c.unassigned++
        if (st === 'aperto') c.open++
        if (st === 'in_lavorazione') c.progress++
        if (st === 'risolto') c.resolved++
      })
      setCounts(c)
    })
    return () => { alive = false }
  }, [view, profile])

  const go = (v) => { if (LIST_VIEWS[v]) setListView(v); if (v !== 'customers') setCustomerId(null); setView(v) }
  const openTicket = (ticket) => {
    if (LIST_VIEWS[view]) setListView(view)
    if (view !== 'ticket') setReturnView(view === 'dashboard' ? listView : view)
    setSelectedTicket(ticket); setView('ticket')
  }
  const goBack = () => { setSelectedTicket(null); setView(returnView) }
  const openCustomer = (id) => { setCustomerId(id); setSelectedTicket(null); setView('customers') }

  if (loading) return <div className="screen-center"><Spinner size={22} /></div>

  if (!session) return <Auth />

  if (!profile) return (
    <div className="screen-center">
      <p>Impossibile caricare il profilo utente.</p>
      <button className="btn btn-primary" onClick={() => supabase.auth.signOut()}>Esci e riprova</button>
    </div>
  )

  const lv = LIST_VIEWS[view]
  return (
    <div className="app">
      <Sidebar view={view} listView={listView} returnView={returnView} go={go} profile={profile} counts={counts} />
      <main className="main">
        <div className="page" key={view === 'ticket' ? `t-${selectedTicket?.id}` : view}>
          {view === 'dashboard' && <Dashboard profile={profile} openTicket={openTicket} setView={go} />}
          {lv && <TicketList profile={profile} openTicket={openTicket} setView={go} scope={lv.scope} status={lv.status || null} title={lv.title} subtitle={lv.subtitle} />}
          {view === 'ticket' && selectedTicket && <TicketDetail ticket={selectedTicket} profile={profile} goBack={goBack} backLabel={returnView === 'customers' ? 'Clienti' : LIST_VIEWS[returnView]?.title} onOpenCustomer={openCustomer} />}
          {view === 'new' && <NewTicket profile={profile} onCreated={() => go('tickets')} />}
          {view === 'settings' && <Settings />}
          {view === 'customers' && <Customers openTicket={openTicket} initialCustomerId={customerId} onSelect={setCustomerId} />}
        </div>
      </main>
    </div>
  )
}

function NavItem({ icon, label, active, onClick, count, hot }) {
  return (
    <button className={`sb-item${active ? ' active' : ''}`} onClick={onClick}>
      <span className="sb-ico"><Icon name={icon} size={16} /></span>
      <span className="sb-label">{label}</span>
      {count != null && <span className={`sb-count${hot && count > 0 ? ' hot' : ''}`}>{count}</span>}
    </button>
  )
}

function Sidebar({ view, listView, returnView, go, profile, counts }) {
  const signOut = () => supabase.auth.signOut()
  // In dettaglio ticket resta evidenziata la vista da cui si e' arrivati
  const current = view === 'ticket' ? (returnView === 'customers' ? 'customers' : listView) : view
  const c = counts || {}

  return (
    <aside className="sidebar">
      <div className="sb-brand">
        <div className="sb-logo"><Icon name="message" size={17} strokeWidth={2.4} /></div>
        <div>
          <div className="sb-name">TicketDesk</div>
          <div className="sb-tag">Assistenza clienti</div>
        </div>
      </div>

      <button className={`sb-new${view === 'new' ? ' active' : ''}`} onClick={() => go('new')}>
        <Icon name="plus" size={16} strokeWidth={2.5} /> Nuovo ticket
      </button>

      <nav className="sb-nav">
        <NavItem icon="dashboard" label="Dashboard" active={current === 'dashboard'} onClick={() => go('dashboard')} />

        <div className="sb-section">Viste</div>
        <NavItem icon="userX" label="Non assegnati" count={c.unassigned} hot active={current === 'unassigned'} onClick={() => go('unassigned')} />
        <NavItem icon="user" label="I miei ticket" count={c['my-tickets']} active={current === 'my-tickets'} onClick={() => go('my-tickets')} />
        <NavItem icon="circleDot" label="Aperti" count={c.open} hot active={current === 'open'} onClick={() => go('open')} />
        <NavItem icon="clock" label="In lavorazione" count={c.progress} active={current === 'progress'} onClick={() => go('progress')} />
        <NavItem icon="checkCircle" label="Risolti" count={c.resolved} active={current === 'resolved'} onClick={() => go('resolved')} />
        <NavItem icon="layers" label="Tutti i ticket" count={c.tickets} active={current === 'tickets'} onClick={() => go('tickets')} />

        <div className="sb-section">Clienti</div>
        <NavItem icon="user" label="Clienti" active={current === 'customers'} onClick={() => go('customers')} />

        <div className="sb-section">Amministrazione</div>
        <NavItem icon="settings" label="Impostazioni" active={current === 'settings'} onClick={() => go('settings')} />
      </nav>

      <div className="sb-user">
        <Avatar name={profile?.name || profile?.email} size={32} />
        <div className="minw0 grow">
          <div className="sb-user-name truncate">{profile?.name}</div>
          <div className="sb-user-mail truncate">{profile?.email}</div>
        </div>
        <button className="sb-logout" onClick={signOut} title="Esci"><Icon name="logout" size={16} /></button>
      </div>
    </aside>
  )
}
